/* src/map/tileMath.ts — Web Mercator projection and slippy-map tile math.
 *
 * CLAUDE.md Geo: this file is one half of a pair. The other half is the SQL function
 * `lonlat_to_chunk_key()` in supabase/migrations/0002_osm_world.sql. If the two ever
 * disagree, the client asks for chunks the server indexed under different keys and the world
 * tears. tests/unit/tileMath.test.ts pins them together against a shared fixture table —
 * keep it passing.
 *
 * Verified against the live game: the observed spawn (151.104, -33.913) maps to chunk key
 * "15/30137/19668", which is the exact key PLAN.md Part 1 §3 recorded from the original.
 */

/** WGS84 semi-major axis, metres. */
export const EARTH_RADIUS_M = 6378137;
export const EARTH_CIRCUMFERENCE_M = 2 * Math.PI * EARTH_RADIUS_M;

/** Latitude beyond which Web Mercator diverges. Standard slippy-map clamp. */
export const MAX_LATITUDE = 85.05112878;

/** Metres per pixel at zoom 0 on the equator, for 256 px tiles. */
export const MPP_ZOOM_0 = EARTH_CIRCUMFERENCE_M / 256;

export interface TileCoord {
  readonly z: number;
  readonly x: number;
  readonly y: number;
}

export function clampLatitude(latDeg: number): number {
  return Math.max(-MAX_LATITUDE, Math.min(MAX_LATITUDE, latDeg));
}

/** lon/lat (EPSG:4326) → Web Mercator metres (EPSG:3857). */
export function project(lonDeg: number, latDeg: number): [number, number] {
  const lat = clampLatitude(latDeg);
  const x = EARTH_RADIUS_M * ((lonDeg * Math.PI) / 180);
  const y = EARTH_RADIUS_M * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 180 / 2));
  return [x, y];
}

/** Web Mercator metres → lon/lat. */
export function unproject(mercX: number, mercY: number): [number, number] {
  const lonDeg = ((mercX / EARTH_RADIUS_M) * 180) / Math.PI;
  const latDeg = ((2 * Math.atan(Math.exp(mercY / EARTH_RADIUS_M)) - Math.PI / 2) * 180) / Math.PI;
  return [lonDeg, latDeg];
}

/** lon/lat → tile x/y at zoom z (floored, i.e. the tile containing the point). */
export function lonLatToTile(lonDeg: number, latDeg: number, z: number): TileCoord {
  const n = 2 ** z;
  const lat = clampLatitude(latDeg);
  const latRad = (lat * Math.PI) / 180;
  const x = Math.floor(((lonDeg + 180) / 360) * n);
  const y = Math.floor(((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2) * n);
  return { z, x, y };
}

/** Tile → lon/lat of its north-west corner. */
export function tileToLonLat(z: number, x: number, y: number): [number, number] {
  const n = 2 ** z;
  const lonDeg = (x / n) * 360 - 180;
  const latDeg = (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * 180) / Math.PI;
  return [lonDeg, latDeg];
}

export interface TileBounds {
  readonly minLon: number;
  readonly minLat: number;
  readonly maxLon: number;
  readonly maxLat: number;
}

/** Tile → its lon/lat bounding box. NW corner is (minLon, maxLat); y grows southward. */
export function tileBoundsLonLat(z: number, x: number, y: number): TileBounds {
  const [minLon, maxLat] = tileToLonLat(z, x, y);
  const [maxLon, minLat] = tileToLonLat(z, x + 1, y + 1);
  return { minLon, minLat, maxLon, maxLat };
}

/** Edge length of a tile at zoom z, in Web Mercator metres. */
export function tileSizeMerc(z: number): number {
  return EARTH_CIRCUMFERENCE_M / 2 ** z;
}

/** Web Mercator metres of the tile's north-west corner. */
export function tileOriginMerc(z: number, x: number, y: number): [number, number] {
  const size = tileSizeMerc(z);
  return [-Math.PI * EARTH_RADIUS_M + x * size, Math.PI * EARTH_RADIUS_M - y * size];
}

/**
 * MVT geometry is expressed in tile-local units (extent × extent, y down from the NW corner).
 * Convert one such coordinate to Web Mercator metres.
 */
export function tileLocalToMerc(
  z: number,
  x: number,
  y: number,
  localX: number,
  localY: number,
  extent = 4096,
): [number, number] {
  const size = tileSizeMerc(z);
  const [ox, oy] = tileOriginMerc(z, x, y);
  return [ox + (localX / extent) * size, oy - (localY / extent) * size];
}

/** Metres per pixel at a zoom and latitude, 256 px tiles. */
export function metresPerPixel(z: number, latDeg: number): number {
  return (MPP_ZOOM_0 * Math.cos((clampLatitude(latDeg) * Math.PI) / 180)) / 2 ** z;
}

/**
 * Web Mercator distances are inflated by 1/cos(latitude) — at Sydney's −33.9° that is about
 * 1.20×. Ground geometry is therefore scaled by cos(lat) so one world unit is one real metre,
 * which matters because building heights come from OSM in real metres. Skip this and every
 * building is ~20% too short for its footprint.
 */
export function mercToMetreScale(latDeg: number): number {
  return Math.cos((clampLatitude(latDeg) * Math.PI) / 180);
}

/** The canonical chunk-key string. Must match `lonlat_to_chunk_key()` in SQL, exactly. */
export function formatChunkKey(z: number, x: number, y: number): string {
  return `${String(z)}/${String(x)}/${String(y)}`;
}

export function lonLatToChunkKey(lonDeg: number, latDeg: number, z = 15): string {
  const t = lonLatToTile(lonDeg, latDeg, z);
  return formatChunkKey(t.z, t.x, t.y);
}

export function parseChunkKey(key: string): TileCoord | null {
  const parts = key.split('/');
  if (parts.length !== 3) return null;
  const z = Number(parts[0]);
  const x = Number(parts[1]);
  const y = Number(parts[2]);
  if (!Number.isInteger(z) || !Number.isInteger(x) || !Number.isInteger(y)) return null;
  return { z, x, y };
}

/**
 * First-pass zoom estimate from orbit distance. A top-down approximation — for a tilted
 * camera it under-estimates ground span near the horizon, so the tile manager widens the
 * result with a frustum-ground intersection rather than trusting this alone
 * (PLAN.md Part 2 §2).
 */
export function tileZoomForCamera(
  distanceM: number,
  viewportHeightPx: number,
  fovYDeg: number,
  latDeg: number,
): number {
  const fovY = (fovYDeg * Math.PI) / 180;
  const groundSpanM = 2 * distanceM * Math.tan(fovY / 2);
  const mpp = groundSpanM / Math.max(1, viewportHeightPx);
  const z = Math.log2(
    (MPP_ZOOM_0 * Math.cos((clampLatitude(latDeg) * Math.PI) / 180)) / Math.max(mpp, 1e-9),
  );
  return Math.max(0, Math.min(22, Math.round(z)));
}
